import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, UserCircle, MagnifyingGlass, UsersThree, Printer } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { toast } from 'sonner';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import BulkUserDialog from '@/components/users/BulkUserDialog';
import UserFormDialog from '@/components/users/UserFormDialog';
import { TRANS_CAP } from '@/components/users/userMeta';
import { UserCardList } from '@/components/users/UserCardList';
import { UserTable } from '@/components/users/UserTable';
import { ResetPasswordDialog } from '@/components/users/ResetPasswordDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_DRAFT = {
  name: '',
  email: '',
  password: '',
  role: 'user',
  is_active: true,
  employee_id: '',
  mfg_department_id: '',
  capability_overrides: {},
};

export default function UsersSettings() {
  const { user: currentUser } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pwReset, setPwReset] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [capKeys, setCapKeys] = useState([]);
  const [roleCaps, setRoleCaps] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [mfgDepartments, setMfgDepartments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, eRes, cRes, dRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/role-capabilities`).catch(() => ({ data: { keys: [], roles: {} } })),
        axios.get(`${API}/mfg/departments`).catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data || []);
      setEmployees(eRes.data || []);
      setMfgDepartments(Array.isArray(dRes.data) ? dRes.data : []);
      setCapKeys(cRes.data?.keys || []);
      setRoleCaps(cRes.data?.roles || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t('users.t.loadFail'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filterRole !== 'all' && u.role !== filterRole) return false;
      if (!q) return true;
      return (
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    });
  }, [users, search, filterRole]);

  const stats = useMemo(() => ({
    total: users.length,
    admin: users.filter((u) => u.role === 'admin').length,
    manager: users.filter((u) => u.role === 'manager').length,
    user: users.filter((u) => u.role === 'user').length,
  }), [users]);

  const startNew = () => {
    setDraft(EMPTY_DRAFT);
    setEditing('new');
  };

  const startEdit = (u) => {
    setDraft({
      name: u.name || '',
      email: u.email || '',
      password: '',
      role: u.role || 'user',
      is_active: u.is_active !== false,
      employee_id: u.employee_id || '',
      mfg_department_id: u.mfg_department_id || '',
      capability_overrides: { ...(u.capability_overrides || {}) },
    });
    setEditing(u.id);
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
  };

  const save = async () => {
    const name = draft.name.trim();
    const email = draft.email.trim().toLowerCase();
    if (!name) return toast.error(t('users.t.nameReq'));
    if (editing === 'new') {
      if (!email) return toast.error(t('users.t.emailReq'));
      if (!draft.password || draft.password.length < 8) return toast.error(t('users.t.pwMin'));
    }
    setSaving(true);
    try {
      const payload = {
        name,
        role: draft.role,
        is_active: draft.is_active,
        employee_id: draft.employee_id || null,
        mfg_department_id: draft.mfg_department_id || null,
        capability_overrides: draft.capability_overrides || {},
      };
      if (editing === 'new') {
        payload.email = email;
        payload.password = draft.password;
        await axios.post(`${API}/users`, payload);
        toast.success(t('users.t.added'));
      } else {
        if (draft.password) {
          if (draft.password.length < 8) return toast.error(t('users.t.pwMin'));
          payload.password = draft.password;
        }
        await axios.put(`${API}/users/${editing}`, payload);
        toast.success(t('users.t.updated'));
      }
      closeEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t('users.t.saveFail'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) return toast.error(t('users.t.delSelf'));
    if (!window.confirm(t('users.t.delConfirm', { name: u.name }))) return;
    try {
      await axios.delete(`${API}/users/${u.id}`);
      toast.success(t('users.t.deleted'));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t('users.t.delFail'));
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`${API}/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? t('users.t.deactivated') : t('users.t.activated'));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t('users.t.updateFail'));
    }
  };

  const openResetPw = (u) => {
    setPwReset(u);
    setResetPw('');
  };

  const closeResetPw = () => {
    setPwReset(null);
    setResetPw('');
  };

  const submitResetPw = async () => {
    if (!resetPw || resetPw.length < 8) return toast.error(t('users.t.pwMin'));
    try {
      await axios.put(`${API}/users/${pwReset.id}`, { password: resetPw });
      toast.success(t('users.t.pwResetDone', { name: pwReset.name }));
      closeResetPw();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t('users.t.resetFail'));
    }
  };

  const employeeName = (id) => employees.find((e) => e.id === id)?.name || '—';

  const listProps = {
    users: filteredUsers,
    currentUserId: currentUser?.id,
    employeeName,
    onToggleActive: handleToggleActive,
    onResetPw: openResetPw,
    onEdit: startEdit,
    onDelete: handleDelete,
  };

  return (
    <div className="space-y-6" data-testid="users-settings-page">
      {/* Header */}
      <PageHeader
        testid="users-page-header"
        title={t('users.title')}
        description={t('users.subtitle')}
        secondary={
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="btn-bulk-users">
            <UsersThree size={16} className="mr-1.5" /> {t('users.bulkAdd')}
          </Button>
        }
        menuItems={[
          { label: t('users.printCreds'), icon: <Printer size={15} />, onClick: () => navigate('/settings/credentials-print'), testid: 'btn-print-credentials' },
        ]}
        primary={
          <Button onClick={startNew} data-testid="btn-add-user">
            <Plus size={16} className="mr-1" /> {t('users.addUser')}
          </Button>
        }
      />

      {/* Toolbar: search + role filter chips (with counts) */}
      <section className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 sm:max-w-md">
          <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('users.searchPh')}
            className="pl-9 h-9"
            data-testid="user-search"
          />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto" data-testid="user-role-chips">
          {[
            { key: 'all', label: t('users.filter.all'), count: stats.total },
            { key: 'admin', label: t('users.role.admin'), count: stats.admin },
            { key: 'manager', label: t('users.role.manager'), count: stats.manager },
            { key: 'user', label: t('users.role.user'), count: stats.user },
          ].map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilterRole(c.key)}
              data-testid={`user-role-chip-${c.key}`}
              className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                filterRole === c.key ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
              }`}
            >
              {c.label}（{c.count}）
            </button>
          ))}
        </div>
      </section>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-lg border border-zinc-200 p-4 space-y-3" data-testid="users-loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 w-full bg-zinc-100 animate-pulse rounded-md" />
          ))}
        </div>
      ) : filteredUsers.length === 0 ? (
        <EmptyState
          testid="user-empty"
          icon={UserCircle}
          title={search || filterRole !== 'all' ? t('users.emptyFiltered') : t('users.empty')}
        />
      ) : (
        <>
          <UserCardList {...listProps} />
          <UserTable {...listProps} />
        </>
      )}

      {/* Create / Edit dialog */}
      <UserFormDialog
        editing={editing}
        draft={draft}
        setDraft={setDraft}
        employees={employees}
        mfgDepartments={mfgDepartments}
        capKeys={capKeys}
        roleCaps={roleCaps}
        transCap={TRANS_CAP}
        saving={saving}
        onClose={closeEdit}
        onSave={save}
      />

      <ResetPasswordDialog
        pwReset={pwReset}
        resetPw={resetPw}
        setResetPw={setResetPw}
        onClose={closeResetPw}
        onSubmit={submitResetPw}
      />

      <BulkUserDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        employees={employees}
        existingUsers={users}
        onCreated={() => { setBulkOpen(false); load(); navigate('/settings/credentials-print'); }}
      />
    </div>
  );
}
